import * as React from 'react';
import {BrowserRouter, Route, Routes} from 'react-router-dom';
import {Box, StyledEngineProvider, ThemeProvider} from '@mui/material';
import './index.css'

import LoginForm from './pages/LoginPage';

import Footer from "./layout/Footer";
import AppLayout from "./layout/AppLayout";
import SocialMediaPostDetailsPage from "./pages/SocialMediaPostDetailsPage";
import OnboardingFlowPage from "./pages/OnboardingFlowPage";
import ProfilePage from "./pages/ProfilePage";
import Register from "./pages/Register";
import WaitlistStatusPage from "./pages/WaitlistStatusPage";
import TemplateEditorPage from "./pages/TemplateEditorPage";
import DashboardPage from "./pages/DashboardPage";
import CalendarPage from "./pages/CalendarPage";
import TemplatesPage from "./pages/TemplatesPage";
import TemplateGeneratorPage from "./pages/TemplateGeneratorPage";
import SocialConnectionPage from "./pages/SocialConnectionPage";
import CheckEmailPage from "./pages/CheckEmailPage";
import {PrivateRoutes} from "./providers/PrivateRouteProvider";
import {AuthProvider, useAuth} from "./providers/AuthProvider";
import {ContentBucketsProvider} from "./providers/ContentBucketsProvider";
import {UserDataProvider} from "./providers/UserDataProvider";
import {TokenValidationProvider} from "./providers/TokenValidationProvider";
import {GeoBlockingProvider} from "./providers/GeoBlockingProvider";

import PricingPage from "./pages/PricingPage";
import PaidPage from "./pages/PaidPage";
import StatusAlert from "./components/Alerts";
import NewPostPage from "./pages/NewPost";
import ContentBucketsPage from "./pages/ContentBucketsPage";
import ExpiredAccountsModal from "./components/ExpiredAccountsModal";

import theme from './theme'
import styled from '@emotion/styled';
import ConfirmEmailPage from "./pages/ConfirmEmailPage";
import SocialConnectionLinkedInPage from "./pages/SocialConnectionLinkedInPage";
import SocialConnectionInstagramPage from "./pages/SocialConnectionInstagramPage";
import SocialConnectionTwitterPage from "./pages/SocialConnectionTwitterPage";
import SocialConnectionTikTok from "./pages/SocialConnectionTikTok";
import SocialConnectionThreads from "./pages/SocialConnectionThreads";
import SocialConnectionLinkedInOrgPage from "./pages/SocialConnectionLinkedInOrgPage";
import {FeatureGuard} from "./components/FeatureGuard";
import TemplateCreate from "./components/TemplateCreate";
import SocialConnectionMastodon from "./pages/SocialConnectionMastodon";
import SocialConnectionFacebook from "./pages/SocialConnectionFacebook";
import PasswordResetRequest from "./pages/PasswordResetRequest";
import PasswordResetConfirm from "./pages/PasswordResetConfirm";
import NotFoundPage from "./pages/NotFoundPage";
import {AnalyticsRouter} from "./components/AnalyticsRouter";
import {AnalyticsProvider} from "./context/AnalyticsContext";
import CampaignCreatorPage from "./pages/CampaignCreatorPage";
import CampaignReviewPage from "./pages/CampaignReviewPage";
import CampaignDashboardPage from "./pages/CampaignDashboardPage";
import CampaignAnalyticsPage from "./pages/CampaignAnalyticsPage";
import CampaignErrorBoundary from "./components/campaigns/CampaignErrorBoundary";
import ContentRecyclingPage from "./pages/ContentRecyclingPage";
import PlatformStrategyPage from "./pages/PlatformStrategyPage";
import AnalyticsDashboardPage from "./pages/AnalyticsDashboardPage";
import {DemoModeProvider} from "./providers/DemoModeProvider";
import EnhancedPostComposer from "./components/EnhancedPostComposer";

const AppWrapper = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    width: 100%;
`;

const ContentContainer = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
`;

const FooterWrapper = styled.div`
    flex-shrink: 0;
    margin-top: auto;
`;

// Inner component that uses auth context
function AppWithAuth() {
    const {user} = useAuth();

    return (
        <AnalyticsProvider>
            <ContentBucketsProvider>
                <UserDataProvider>
                    <TokenValidationProvider>
                        <AnalyticsRouter/>
                        <AppWrapper>
                            <ContentContainer>
                                <AppLayout>
                                    <StatusAlert/>
                                    <ExpiredAccountsModal />
                                    <Routes>
                                                <Route exact path="/" element={<LoginForm/>}/>
                                                <Route exact path="/login" element={<LoginForm/>}/>
                                                <Route exact path="/signup" element={<Register/>}/>
                                                <Route exact path="/waitlist/status" element={<WaitlistStatusPage/>}/>
                                                <Route exact path="/onboarding" element={<OnboardingFlowPage/>}/>
                                                <Route path="/forgot-password" element={<PasswordResetRequest/>}/>
                                                <Route path="/forgot-password/:token" element={<PasswordResetConfirm/>}/>
                                                <Route exact path="/pricing" element={<PricingPage/>}/>
                                                <Route exact path="/paid" element={<PaidPage/>}/>
                                                <Route exact path="/check-email" element={<CheckEmailPage/>}/>
                                                <Route exact path="/confirm-email/:token" element={<ConfirmEmailPage/>}/>
                                                
                                                {/* Campaign Routes */}
                                                <Route exact path="/campaigns" element={
                                                    <PrivateRoutes userDetail={user?.[0]}>
                                                        <CampaignErrorBoundary>
                                                            <CampaignDashboardPage/>
                                                        </CampaignErrorBoundary>
                                                    </PrivateRoutes>
                                                }/>
                                                <Route exact path="/campaigns/create" element={
                                                    <PrivateRoutes userDetail={user?.[0]}>
                                                        <CampaignErrorBoundary>
                                                            <CampaignCreatorPage/>
                                                        </CampaignErrorBoundary>
                                                    </PrivateRoutes>
                                                }/>
                                                <Route exact path="/campaigns/:campaignId/review" element={
                                                    <PrivateRoutes userDetail={user?.[0]}>
                                                        <CampaignErrorBoundary>
                                                            <CampaignReviewPage/>
                                                        </CampaignErrorBoundary>
                                                    </PrivateRoutes>
                                                }/>
                                                <Route exact path="/campaigns/:campaignId/analytics" element={
                                                    <PrivateRoutes userDetail={user?.[0]}>
                                                        <CampaignErrorBoundary>
                                                            <CampaignAnalyticsPage/>
                                                        </CampaignErrorBoundary>
                                                    </PrivateRoutes>
                                                }/>

                                                {/* Content & Analytics Routes */}
                                                <Route exact path="/analytics" element={
                                                    <PrivateRoutes userDetail={user?.[0]}>
                                                        <FeatureGuard requiredPermission="canCreatePosts">
                                                            <AnalyticsDashboardPage/>
                                                        </FeatureGuard>
                                                    </PrivateRoutes>
                                                }/>
                                                <Route exact path="/content-recycling" element={
                                                    <PrivateRoutes userDetail={user?.[0]}>
                                                        <FeatureGuard requiredPermission="canCreatePosts">
                                                            <ContentRecyclingPage/>
                                                        </FeatureGuard>
                                                    </PrivateRoutes>
                                                }/>
                                                <Route exact path="/platform-strategy" element={
                                                    <PrivateRoutes userDetail={user?.[0]}>
                                                        <FeatureGuard requiredPermission="canCreatePosts">
                                                            <PlatformStrategyPage/>
                                                        </FeatureGuard>
                                                    </PrivateRoutes>
                                                }/>

                                                {/* Core Feature Routes */}
                                                <Route exact path="/post/create" element={<PrivateRoutes
                                                    userDetail={user?.[0]}>
                                                    <FeatureGuard requiredPermission="canCreatePosts">
                                                        <EnhancedPostComposer/>
                                                        {/*<NewPostPage />*/}
                                                    </FeatureGuard>
                                                </PrivateRoutes>}/>
                                                <Route exact path="/content-buckets" element={<PrivateRoutes
                                                    userDetail={user?.[0]}>
                                                    <FeatureGuard requiredPermission="canCreatePosts">
                                                        <ContentBucketsPage/>
                                                    </FeatureGuard>
                                                </PrivateRoutes>}/>
                                                <Route exact path="/calendar" element={<PrivateRoutes
                                                    userDetail={user?.[0]}>
                                                    <FeatureGuard requiredPermission="canCreatePosts">
                                                        <CalendarPage/>
                                                    </FeatureGuard>
                                                </PrivateRoutes>}/>
                                                <Route exact path="/templates" element={<PrivateRoutes
                                                    userDetail={user?.[0]}>
                                                    <FeatureGuard requiredPermission="canAccessTemplateEditor">
                                                        <TemplatesPage/>
                                                    </FeatureGuard>
                                                </PrivateRoutes>}/>
                                                <Route exact path="/editor/:tempId?"
                                                       element={<PrivateRoutes userDetail={user?.[0]}>
                                                           <FeatureGuard
                                                               requiredPermission="canAccessTemplateEditor"
                                                               fallbackPath="/dashboard"
                                                               showError={true}>
                                                               <TemplateEditorPage/>
                                                           </FeatureGuard>
                                                       </PrivateRoutes>

                                                       }/>
                                                <Route exact path="/template/create/:templateId?"
                                                       element={<PrivateRoutes userDetail={user?.[0]}>
                                                           <TemplateCreate
                                                               requiredPermission="canAccessTemplateEditor"
                                                               fallbackPath="/dashboard"
                                                               showError={true}>
                                                           </TemplateCreate>
                                                       </PrivateRoutes>

                                                       }/>
                                                
                                                {/* User & Social Routes */}
                                                <Route exact path="/post/:id" element={<PrivateRoutes
                                                    userDetail={user?.[0]}><SocialMediaPostDetailsPage/></PrivateRoutes>}/>
                                                <Route exact path="/profile" element={<PrivateRoutes
                                                    userDetail={user?.[0]}><ProfilePage/></PrivateRoutes>}/>
                                                <Route exact path="/dashboard" element={<PrivateRoutes
                                                    userDetail={user?.[0]}><DashboardPage/></PrivateRoutes>}/>
                                                <Route exact path="/generate" element={<PrivateRoutes
                                                    userDetail={user?.[0]}><TemplateGeneratorPage/></PrivateRoutes>}/>
                                                
                                                {/* Social Connection Routes */}
                                                <Route exact path="/connect/socials" element={<PrivateRoutes
                                                    userDetail={user?.[0]}><SocialConnectionPage/></PrivateRoutes>}/>
                                                <Route exact path="/connect/linkedin/callback" element={<PrivateRoutes
                                                    userDetail={user?.[0]}><SocialConnectionLinkedInPage/></PrivateRoutes>}/>
                                                <Route exact path="/connect/ig/callback" element={<PrivateRoutes
                                                    userDetail={user?.[0]}><SocialConnectionInstagramPage/></PrivateRoutes>}/>
                                                <Route exact path="/connect/twitter/callback" element={<PrivateRoutes
                                                    userDetail={user?.[0]}><SocialConnectionTwitterPage/></PrivateRoutes>}/>
                                                <Route exact path="/connect/tiktok/callback" element={<PrivateRoutes
                                                    userDetail={user?.[0]}><SocialConnectionTikTok/></PrivateRoutes>}/>
                                                <Route exact path="/connect/threads/callback" element={<PrivateRoutes
                                                    userDetail={user?.[0]}><SocialConnectionThreads/></PrivateRoutes>}/>
                                                <Route
                                                      path="/connect/linkedin/business/callback"
                                                      element={<SocialConnectionLinkedInOrgPage />}/>
                                                <Route exact path="/connect/mastodon/callback" element={<PrivateRoutes
                                                    userDetail={user?.[0]}><SocialConnectionMastodon/></PrivateRoutes>}/>
                                                <Route exact path="/connect/facebook/callback" element={<PrivateRoutes
                                                    userDetail={user?.[0]}><SocialConnectionFacebook/></PrivateRoutes>}/>
                                                
                                                <Route path="*" element={<NotFoundPage/>}/>
                                            </Routes>
                                        </AppLayout>
                                    </ContentContainer>
                                    <FooterWrapper>
                                        <Footer/>
                                    </FooterWrapper>
                                </AppWrapper>
                            </TokenValidationProvider>
                        </UserDataProvider>
                    </ContentBucketsProvider>
                </AnalyticsProvider>
    );
}

export default function App() {
    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
                <GeoBlockingProvider>
                    <DemoModeProvider>
                        <BrowserRouter>
                            <AuthProvider>
                                <AppWithAuth />
                            </AuthProvider>
                        </BrowserRouter>
                    </DemoModeProvider>
                </GeoBlockingProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
}
